import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Result, Spin } from "antd";
import { AgGridReact } from "ag-grid-react";
import { ProjectContext } from "context/ProjectProvider";
import { getFeatureEntityLog } from "services/feature-apis";
import { FeatureTypes } from "entity-app/constants";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import { CUSTOM_EXCEL_EXPORT_FLAG } from "./constants";
import {
  buildCustomExportColumnDefs,
  LEAF_COLUMN_COUNT,
  CUSTOM_EXPORT_DEFAULT_COL_DEF,
  CUSTOM_EXPORT_EXCEL_STYLES
} from "./material-export-columns";
import { mapMaterialRows } from "./material-export-mapping";

function CustomExcelExportView() {
  const {
    isFeatureFlagEnabled,
    isProductEntitlementsDataAvailable,
    tokenRetrievalState,
    projectDetails
  } = useContext(ProjectContext);
  const gridRef = useRef<AgGridReact>(null);

  const [materials, setMaterials] = useState<unknown[] | null>(null);
  const [failed, setFailed] = useState(false);

  // Entitlements arrive asynchronously and isFeatureFlagEnabled reports false until they do,
  // so the "not enabled" screen must wait for a real answer rather than flash on every load.
  const entitlementsKnown = isProductEntitlementsDataAvailable;
  const enabled = isFeatureFlagEnabled(CUSTOM_EXCEL_EXPORT_FLAG);

  useEffect(() => {
    document.title = "Custom Export - ConstructivIQ";
  }, []);

  useEffect(() => {
    if (!enabled || !tokenRetrievalState?.token) return undefined;
    let cancelled = false;

    getFeatureEntityLog({
      projectToken: tokenRetrievalState.token,
      featureType: FeatureTypes.MATERIALS
    })
      .then((response) => {
        if (cancelled) return;
        if (response.success && Array.isArray(response.data)) {
          setMaterials(response.data);
        } else {
          setFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, tokenRetrievalState?.token]);

  const rowData = useMemo(
    () => (materials ? mapMaterialRows(materials) : []),
    [materials]
  );
  const columnDefs = useMemo(() => buildCustomExportColumnDefs(), []);

  // Same shape as the Material Log's print/export title.
  const reportTitle = `${
    projectDetails?.name || "Project"
  } - Material Log Custom Report - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
    `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
  )}`;

  const fileName =
    `${projectDetails?.name ?? "Material Log"} - Custom Export - ${DateUtils.format(
      new Date(),
      DATE_FORMAT_MMDDYYYY
    )}`.replace(/[\\/:*?"<>|]/g, "-");

  if (!entitlementsKnown) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin description="Preparing your export…" />
      </div>
    );
  }

  if (!enabled) {
    return (
      <Result
        status="403"
        title="Custom export is not enabled"
        subTitle="Ask your project administrator to enable the custom export for this project."
      />
    );
  }

  if (failed) {
    return (
      <Result
        status="warning"
        title="We could not load the material data"
        subTitle="Close this tab and try again from the Material Log."
      />
    );
  }

  if (materials === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin description="Preparing your export…" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-white">
      <div className="flex items-center gap-3 px-4 py-3 border-0 border-b border-solid border-ciq-gray-200">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate" title={reportTitle}>
            {reportTitle}
          </div>
          <div className="text-[11px] text-ciq-gray-600">
            {rowData.length} materials
          </div>
        </div>
        <Button
          type="primary"
          className="ml-auto"
          data-testid="custom-excel-export-download"
          disabled={rowData.length === 0}
          onClick={() =>
            gridRef.current?.api?.exportDataAsExcel({
              fileName: `${fileName}.xlsx`,
              sheetName: "Material Export",
              prependContent: [
                {
                  cells: [
                    {
                      styleId: "customHeader",
                      data: { type: "String", value: reportTitle },
                      mergeAcross: LEAF_COLUMN_COUNT - 1
                    }
                  ]
                }
              ]
            })
          }
        >
          Export to Excel
        </Button>
      </div>

      <div className="flex-1 min-h-0 ag-theme-alpine">
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={CUSTOM_EXPORT_DEFAULT_COL_DEF}
          excelStyles={CUSTOM_EXPORT_EXCEL_STYLES}
          groupHeaderHeight={34}
          headerHeight={44}
          rowHeight={34}
          enableCellTextSelection
          suppressDragLeaveHidesColumns
          overlayNoRowsTemplate="No materials to export"
        />
      </div>
    </div>
  );
}

export default CustomExcelExportView;
